import React, { useEffect, useState } from 'react';
import { useData } from '../../hooks/useData';
import { Box, CircularProgress, Grid, Link, List, ListItem, ListItemProps, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SHAutocomplete, { Option } from '../form/SHAutocomplete';
import { useWatch } from 'react-hook-form';
import SHFormTextField from '../form/SHFormTextField';

const StyledListItem = styled(ListItem)<ListItemProps>(({theme}) => ({
    padding: '1em'
}));

interface Props {
    categories: Option[],
    alphabet: Option[],
    years: Option[]
}

export interface DocumentListResponse {
    documentList: Array<Document>, 
    numberOfRows: number
}

export interface Document {
    fileName?: string, 
    id?: number,
    category?: ("politics" | "science" | "news"),
    language?: ("de" | "en"),
    year?: string
}

const DocumentList : React.FC<Props> = ({categories, alphabet, years}) => {
    const {t} = useTranslation('common');
    const [documents, setDocuments] = useState([] as Array<Document>);
    const [lastDocQuery, setLastDocQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined as string | undefined);
    const category = useWatch({name: "docList.category"});
    const year = useWatch({name: "docList.year"});
    const startsWith = useWatch({name: "docList.startsWith"});
    const page = useWatch({name: "docList.page"});
    const numberOfRows = 20;
    const searchTerm = useWatch({name: "docList.searchTerm"});
    const [maxNumberOfRows, setMaxNumberOfRows] = useState(0);
    const [docQuery, setDocQuery] = useState("documents?startsWith=A&numberOfRows=20");
    const pageCount = maxNumberOfRows >= numberOfRows ? Math.ceil(numberOfRows/maxNumberOfRows) : 1;
    const startIndex = ((Number.parseInt(page.key)??1)-1)*numberOfRows;

    const pages = [] as Option[];
    for(let i = 1; i <= pageCount; i++) {
        pages.push({key: `${i}`, label: `${i}`});
    }

    const [getDocumentsApiCall] = useData<DocumentListResponse>(
        docQuery, 
        "GET", 
        "application/json"
    );

    useEffect(() => {
        const getQueryParamString = (name: string, value: {key: string, label: string} | string | undefined) => {
            if(value instanceof Object && value.key) {
                return `${value?.key ? `${name}=${value?.key}&` : ''}`;
            }
            else {
                return `${value && value !== '' ? `${name}=${value}&` : ''}`;
            }
        }
        const newDocQuery = `documents?${getQueryParamString('searchTerm', searchTerm)}${getQueryParamString('startsWith', startsWith)}${getQueryParamString('category', category)}${getQueryParamString('year', year)}${getQueryParamString('startIndex', `${startIndex}`)}${getQueryParamString('numberOfRows', `${numberOfRows}`)}`;
        if(docQuery !== newDocQuery) {
            setDocQuery(newDocQuery);
        }
    }, [searchTerm, startsWith, category, year, page, numberOfRows, docQuery,startIndex, setDocQuery]);

    useEffect(() => {
        if( !loading && lastDocQuery !== docQuery) {
            setLoading(true);
            getDocumentsApiCall((docs) => {
                if(docs?.documentList) {
                    setDocuments(docs?.documentList);
                }
                if(docs?.numberOfRows) {
                    setMaxNumberOfRows(docs?.numberOfRows);
                }
                setLastDocQuery(docQuery);
                setLoading(false);
            }, (err) => {
                setError(err);
                setLastDocQuery(docQuery);
                setLoading(false);
            }, undefined);
        }
    }, [documents, loading, error, lastDocQuery, docQuery, getDocumentsApiCall]);

    return <>
        <Typography variant='h2'>{t('common.documents.list.title')}</Typography>
        <Grid container gap='1em' sx={{marginTop: '1em'}}>
            <Grid item xs={12} sm={2} >
                <SHAutocomplete name="docList.year" options={years} label={t("common.year")} />
            </Grid>
            <Grid item xs={12} sm={3} >
                <SHAutocomplete name="docList.category" options={categories} label={t("common.category")} />
            </Grid>
            <Grid item xs={12} sm={2} >
                <SHAutocomplete name="docList.startsWith" options={alphabet} label={t("common.letter")} />
            </Grid>
            <Grid item xs={12} sm={2}>
                <SHAutocomplete name="docList.page" options={pages && pages.length > 0 ? pages : [{key: "1", label: "1"}]} label={t("common.page")} />
            </Grid>
            <Grid item xs={12} sm={12} >
                <SHFormTextField name="docList.searchTerm" label={t("common.searchTerm")} />
            </Grid>
        </Grid>
        {!loading && !!documents && documents.length > 0 && <List>
            {documents?.map(doc => <StyledListItem key={doc?.fileName} ><Link href={`${window.location.origin}/api/document/download/${doc?.id}`} target='__blank'>{doc?.fileName} - {doc?.year}</Link></StyledListItem>)}
        </List>}
        {loading === true && <Box sx={{textAlign: 'center'}}><CircularProgress /></Box>}
    </>
}

export default DocumentList;